import { Card, Column, Radio, Row, ToolTip, expandBorderColors, marginUtil, paddingUtil } from "@gadgetinc/widgets";
import { FailureCircleIcon } from "@gadgetinc/widgets/src/icons/FailureCircleIcon";
import { SuccessCircleIcon } from "@gadgetinc/widgets/src/icons/SuccessCircleIcon";
import { useStyletron } from "baseui";
import { ARTWORK_SIZES, ListItem } from "baseui/list";
import { ParagraphSmall } from "baseui/typography";
import { compact, defaultTo, isNil } from "lodash";
import type { ReactElement, ReactNode } from "react";
import React, { Children, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { HotKeys } from "react-hotkeys-ce";
import type { StyleObject } from "styletron-react";
import { useFocus, useHover } from "../../../lib/hooks";

export enum ALIGN {
  Top = "Top",
  Bottom = "Bottom",
}

export enum KIND {
  Stacked = "Stacked",
  Inline = "Inline",
}

export enum DIRECTION {
  Row = "Row",
  Column = "Column",
}

type BaseRadioProps = {
  children?: ReactNode;
  value: string;
  label?: string;
  style?: StyleObject;
  align?: ALIGN;
  spaced?: boolean;
  skipAutoFocus?: boolean;
  kind?: KIND;
  content?: ReactNode;
  dataTestId?: string;
  hideRadio?: boolean;
  startEnhancer?: ReactNode;
  endEnhancer?: ReactNode;
  direction?: DIRECTION;
  padding?: string;
  overrides?: {
    Card?: StyleObject;
    Row?: StyleObject;
  };
  skipPressedEffect?: boolean;
};

/** Tooltip is required when the radio card is disabled, for accessibility purposes */
type RadioCardProps = BaseRadioProps & ({ tooltip: string; disabled: true } | { tooltip?: string; disabled?: boolean });

export const RadioCard = (props: RadioCardProps) => {
  const [css, $theme] = useStyletron();
  const headGap = $theme.sizing.scale600;
  const { value, focusedValue, defaultFocusValue, onChange } = useContext(RadioCardContext);
  const ref = useRef<HTMLDivElement | null>(null);

  const [hasFocus, focusProps, hasFocusWithin] = useFocus(ref);
  const [isHovered, hoverProps] = useHover();
  const pressed = useRef(false);
  const isDisabled = props.disabled;
  const isChecked = value == props.value;
  const isFocused = hasFocus && !hasFocusWithin && !pressed.current;
  const hasFocusValue = !isDisabled && !props.skipAutoFocus && focusedValue == props.value;
  const isFocusable = !value && !focusedValue && props.value === defaultFocusValue;
  const align = props.align ?? ALIGN.Top;
  const showRadio = !props.hideRadio;
  const isActive = !isDisabled && !isChecked && pressed.current;
  const columnDirection = props.direction ?? DIRECTION.Column;

  const borderColor = isDisabled ? $theme.colors.border : $theme.colors.primary;
  const checkedStyles = {
    ...expandBorderColors(borderColor),
    outlineColor: borderColor,
    backgroundColor: $theme.colors.alpha50,
    outlineWidth: "1px",
  };

  const hoveredStyles = {
    backgroundColor: $theme.colors.alpha50,
    ...expandBorderColors($theme.colors.alpha400),
  };

  const activeStyles = {
    backgroundColor: $theme.colors.alpha100,
    ...expandBorderColors($theme.colors.alpha500),
  };

  const radioSize = "16px";
  const innerRadioSize = isChecked ? "6px" : "14px";

  const clickHandler = (event: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    const isKeyEvent = event.type === "keydown";
    const key = "key" in event ? event.key : "";
    const target = event.target as HTMLDivElement;

    // We need to check if the click was on an internal action, like a link or button. If so,
    // then we do _not_ want to select the radio button.
    const parent = target.closest("a, button, .RadioCardGroup");
    const isInnerAction = !!parent && ["a", "button"].includes(parent.tagName.toLowerCase());
    // Inner actions are not considered to be on this radio card — they serve some other purpose,
    // like a link to some documentation.
    const isActionOnCard = target === ref.current || !isInnerAction;
    const isValidAction = !isDisabled && isActionOnCard && (!isKeyEvent || ["Enter", " "].includes(key));

    if (isValidAction) onChange(props.value);
    if (!isKeyEvent) pressed.current = true;
  };

  useEffect(() => {
    // If this card's `value` is focused, then focus on this card's DOM element
    if (hasFocusValue) {
      setTimeout(() => ref?.current?.focus());
    }
  }, [hasFocusValue]);

  const padding = props.padding ?? $theme.sizing.scale500;

  return (
    <ToolTip
      overrides={{
        Outer: {
          style: { display: "flex", flex: 1 },
        },
        Inner: {
          style: { display: "flex", width: "100%" },
        },
      }}
      label={isDisabled ? props.tooltip ?? "Option currently unavailable" : undefined}
    >
      <div
        tabIndex={isChecked || isDisabled || isFocusable ? 0 : -1}
        className={css({ display: "flex", flexGrow: 1, ":focus-visible": { outline: "none" } })}
        onClick={clickHandler}
        onMouseDownCapture={() => (pressed.current = true)}
        onMouseUpCapture={() => (pressed.current = false)}
        onMouseLeave={(event) => {
          pressed.current = false;
          hoverProps.onMouseLeave(event);
        }}
        onMouseEnter={hoverProps.onMouseEnter}
        onBlurCapture={() => (pressed.current = false)}
        onKeyDown={clickHandler}
        {...focusProps}
        ref={ref}
      >
        <Card
          variant="secondary"
          data-testid={props.dataTestId}
          $active={isChecked}
          $focused={isFocused && !isChecked}
          $spaced={defaultTo(props.spaced, false)}
          $style={{
            cursor: props.disabled ? "not-allowed" : "pointer",
            position: "relative",
            boxSizing: "content-box",
            flexGrow: 1,
            boxShadow: "unset",
            ...paddingUtil(padding),
            paddingTop: isActive && !props.skipPressedEffect ? `calc(${padding} + 1px)` : padding,
            paddingBottom: isActive && !props.skipPressedEffect ? `calc(${padding} - 1px)` : padding,
            ...(!isDisabled && !isChecked && isHovered ? hoveredStyles : {}),
            ...(isActive ? activeStyles : {}),
            ...(isChecked ? checkedStyles : {}),
            ...props.style,
            ...props.overrides?.Card,
          }}
        >
          {props.kind === KIND.Inline ? (
            <Row
              $as="article"
              $style={{
                gap: $theme.sizing.scale500,
                alignItems: "start",
                width: "100%",
                ...props.overrides?.Row,
              }}
            >
              {props.startEnhancer}
              {showRadio && (
                <Radio
                  disabled={isDisabled}
                  tabIndex="-1"
                  value={props.value}
                  checked={isChecked}
                  overrides={{
                    Root: {
                      style: {
                        ...marginUtil("0px"),
                        minHeight: $theme.sizing.scale700,
                      },
                    },
                    RadioMarkOuter: {
                      style: {
                        ...marginUtil("0px"),
                        height: radioSize,
                        width: radioSize,
                        backgroundColor: isChecked || isActive || isHovered ? $theme.colors.neutralMax : $theme.colors.primary500,
                      },
                    },
                    RadioMarkInner: {
                      style: {
                        ...marginUtil("0px"),
                        height: innerRadioSize,
                        width: innerRadioSize,
                        backgroundColor: isActive
                          ? $theme.colors.primary200
                          : isHovered
                          ? $theme.colors.primary100
                          : $theme.colors.backgroundPrimary,
                      },
                    },
                  }}
                />
              )}
              <Column $gap={$theme.sizing.scale300} $style={{ flex: 1 }}>
                <ParagraphSmall $style={{ color: isDisabled ? $theme.colors.contentSecondary : $theme.colors.contentPrimary }}>
                  {props.label}
                </ParagraphSmall>
                {props.content}
              </Column>
              {props.endEnhancer}
            </Row>
          ) : columnDirection === DIRECTION.Column ? (
            <Column $style={{ flexGrow: 1, justifyContent: align === ALIGN.Bottom ? "flex-end" : "flex-start" }}>
              {props.children && <div className={css({ ...marginUtil(0), marginBottom: $theme.sizing.scale500 })}>{props.children}</div>}
              <Row
                $as="header"
                $style={{
                  justifyContent: "center",
                  pointerEvents: "none",
                  bottom: headGap,
                  width: "100%",
                }}
              >
                <ParagraphSmall $style={{ color: isDisabled ? $theme.colors.contentSecondary : $theme.colors.contentAccent }}>
                  {props.label}
                </ParagraphSmall>
                {showRadio && (
                  <RadioForRadioCard
                    isDisabled={isDisabled}
                    value={props.value}
                    isChecked={isChecked}
                    isHovered={isHovered}
                    isActive={isActive}
                    radioSize={radioSize}
                    innerRadioSize={innerRadioSize}
                  />
                )}
              </Row>
            </Column>
          ) : (
            <Column $style={{ flexGrow: 1, justifyContent: align === ALIGN.Bottom ? "flex-end" : "flex-start" }}>
              <Row $style={{ gap: $theme.sizing.scale500 }}>
                {showRadio && (
                  <RadioForRadioCard
                    isDisabled={isDisabled}
                    value={props.value}
                    isChecked={isChecked}
                    isHovered={isHovered}
                    isActive={isActive}
                    radioSize={radioSize}
                    innerRadioSize={innerRadioSize}
                  />
                )}
                {props.children && <div className={css({ ...marginUtil(0) })}>{props.children}</div>}
              </Row>
            </Column>
          )}
        </Card>
      </div>
    </ToolTip>
  );
};

const RadioForRadioCard = (props: {
  isDisabled: boolean | undefined;
  value: string;
  isChecked: boolean;
  radioSize: string;
  innerRadioSize: string;
  isHovered: boolean;
  isActive: boolean;
}) => {
  const { isDisabled, isChecked, isHovered, isActive, value, radioSize, innerRadioSize } = props;

  const [_, $theme] = useStyletron();

  return (
    <Radio
      disabled={isDisabled}
      tabIndex="-1"
      value={value}
      checked={isChecked}
      overrides={{
        Root: {
          style: marginUtil("0px"),
        },
        RadioMarkOuter: {
          style: {
            ...marginUtil("0px"),
            height: radioSize,
            width: radioSize,
            backgroundColor: isChecked || isActive || isHovered ? $theme.colors.neutralMax : $theme.colors.primary500,
          },
        },
        RadioMarkInner: {
          style: {
            ...marginUtil("0px"),
            height: innerRadioSize,
            width: innerRadioSize,
            backgroundColor: isActive ? $theme.colors.primary200 : isHovered ? $theme.colors.primary100 : $theme.colors.backgroundPrimary,
          },
        },
      }}
    />
  );
};

export interface RadioCardGroupProps {
  value?: string;
  onChange: (value: string) => void;
  children: ReactElement[];
  defaultValue?: string;
  autoFocusValue?: string;
  style?: StyleObject;
  kind?: KIND;
  direction?: DIRECTION;
}
const RadioCardContext = createContext<
  Pick<RadioCardGroupProps, "value" | "onChange"> & { focusedValue?: string; defaultFocusValue?: string }
>({
  onChange: () => undefined,
});

const defaultIndex = 0;

/** Passing `value` will set the group into controlled mode, and vice versa */
export const RadioCardGroup = (props: RadioCardGroupProps) => {
  const [css, $theme] = useStyletron();
  const [innerValue, setInnerValue] = useState<string | undefined>(props.value ?? props.autoFocusValue ?? props.defaultValue);

  const kindStyles: StyleObject =
    props.kind === KIND.Inline
      ? {
          display: "flex",
          flexDirection: props.direction === DIRECTION.Row ? "row" : "column",
          gap: $theme.sizing.scale500,
        }
      : {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(264px, 1fr))",
          gap: $theme.sizing.scale800,
        };

  // Get array of child options
  const options: Array<{
    value: string;
    index: number;
  }> = useMemo(
    () =>
      compact(
        Children.map(props.children, (child: ReactElement, index) => {
          const name = typeof child.type === "string" ? child.type ?? "" : "name" in child.type ? child.type.name : "";
          if (name === "RadioCard" && !child.props.disabled) {
            return { index, value: child.props.value };
          }
        })
      ),
    [props.children]
  );
  const reverseOptions = useMemo(() => [...options].reverse(), [options]);

  // Use the first item as the default focus value
  const isAutoFocusValueEnabled = options.some(({ value }) => value == props.autoFocusValue);
  const defaultFocusValue = isAutoFocusValueEnabled ? props.autoFocusValue : undefined;
  const [focusedValue, setFocusedValue] = useState<string | undefined>(defaultFocusValue);

  const prevValue = () => {
    if (!options.length) {
      return;
    }
    setInnerValue((current) => {
      const currentIndex = current ? options.findIndex(({ value }) => current === value) : defaultIndex;
      const prevIndex = isNil(currentIndex) ? 0 : currentIndex - 1;
      const newOption = prevIndex < 0 ? options[options.length - 1] : reverseOptions.find((option) => option.index <= prevIndex);
      const newValue = newOption?.value || current;
      if (newValue) props.onChange(newValue);
      setFocusedValue(newValue);
      return newValue;
    });
  };
  const nextValue = () => {
    if (!options.length) {
      return;
    }
    setInnerValue((current) => {
      const currentIndex = current ? options.findIndex(({ value }) => current === value) : defaultIndex;
      let nextIndex = isNil(currentIndex) ? 0 : currentIndex + 1;
      // Cycle back around
      if (nextIndex >= options.length) {
        nextIndex = 0;
      }
      const newOption = options.find((option, index) => index === options.length - 1 || option.index >= nextIndex) ?? options[0];
      const newValue = newOption.value;
      if (newValue) props.onChange(newValue);
      setFocusedValue(newValue);
      return newValue;
    });
  };

  // Keep innerValue in sync, for controlled usages
  useEffect(() => {
    setInnerValue(props.value);
    setFocusedValue(props.value);
  }, [props.value]);

  return (
    <HotKeys
      handlers={{
        PREV: () => prevValue(),
        NEXT: () => nextValue(),
      }}
      keyMap={{
        PREV: ["ArrowLeft", "ArrowUp"],
        NEXT: ["ArrowRight", "ArrowDown"],
      }}
    >
      <RadioCardContext.Provider
        value={{
          defaultFocusValue: props.autoFocusValue || props.defaultValue || options[defaultIndex]?.value || "",
          focusedValue,
          value: innerValue,
          onChange: (value: string) => {
            setInnerValue(value);
            if (value) props.onChange(value);
          },
        }}
      >
        <section
          className={`RadioCardGroup ${css({
            ...kindStyles,
            marginBottom: $theme.sizing.scale300,
            ...props.style,
          })}`}
        >
          {props.children}
        </section>
      </RadioCardContext.Provider>
    </HotKeys>
  );
};

export const RadioCardListItem = (props: { children: ReactNode }) => {
  const [_, $theme] = useStyletron();
  return (
    <ListItem
      artwork={() => <span>•</span>}
      overrides={{
        Root: { style: { alignItems: "start", backgroundColor: "transparent" } },
        Content: { style: { borderBottomStyle: "hidden", minHeight: "0px", width: "100%" } },
        ArtworkContainer: { style: { marginRight: $theme.sizing.scale600, width: $theme.sizing.scale300 } },
      }}
    >
      {props.children}
    </ListItem>
  );
};

export const RadioCardStatusListItem = (props: { children: ReactNode; status: "SUCCESS" | "ERROR" }) => {
  return (
    <ListItem
      artwork={() => (props.status === "SUCCESS" ? <SuccessCircleIcon /> : <FailureCircleIcon />)}
      artworkSize={ARTWORK_SIZES.MEDIUM}
      overrides={{
        Root: { style: { marginBottom: "6px", backgroundColor: "transparent" } },
        Content: { style: { borderBottomStyle: "hidden", minHeight: "20px", width: "100%" } },
        ArtworkContainer: { style: { width: "16px", marginRight: "10px" } },
      }}
    >
      {props.children}
    </ListItem>
  );
};
